/**
 * Clients page
 * Main page for managing clients with detail sidebar
 */

import { useState } from "react";
import { Icon, ClientModal } from "@/components/ui";
import styles from "./ClientsPage.module.css";

interface Client {
  id: string;
  nif: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  status: "active" | "inactive";
  lastService?: string;
}

interface Service {
  id: string;
  date: string;
  title: string;
  description: string;
  status: "completed" | "scheduled";
}

interface Document {
  id: string;
  name: string;
  date: string;
  size: string;
}

const mockClients: Client[] = [
  {
    id: "1",
    nif: "B-12345678",
    name: "Hotel Míramar",
    contactPerson: "Laura Gómez",
    email: "laura@hotelmiramar.com",
    phone: "+34 912 345 678",
    address: "Av. de la Playa 12",
    city: "Madrid",
    province: "Madrid",
    postalCode: "28001",
    status: "active",
  },
  {
    id: "2",
    nif: "B-87654321",
    name: "Restaurante El Olivo",
    contactPerson: "Carlos Ruiz",
    email: "carlos@elolivo.com",
    phone: "+34 934 567 890",
    address: "C/ Mayor 45",
    city: "Barcelona",
    province: "Barcelona",
    postalCode: "08001",
    status: "active",
  },
  {
    id: "3",
    nif: "A-11223344",
    name: "Clínica San José",
    contactPerson: "María Torres",
    email: "info@clinicasanjose.es",
    phone: "+34 956 789 012",
    address: "Av. del Cid 23",
    city: "Valencia",
    province: "Valencia",
    postalCode: "46001",
    status: "active",
  },
  {
    id: "4",
    nif: "A-99887766",
    name: "Supermercados Del Norte",
    contactPerson: "Antonio Vega",
    email: "antonio@delnorte.es",
    phone: "+34 982 345 678",
    address: "Polígono Indus. 8-10",
    city: "Sevilla",
    province: "Sevilla",
    postalCode: "41001",
    status: "inactive",
  },
];

const mockServices: Service[] = [
  {
    id: "1",
    date: "16 Nov 2023",
    title: "Revisión Trimestral DDD",
    description: "Inspección - Desinfección",
    status: "scheduled",
  },
  {
    id: "2",
    date: "30 Ago 2023",
    title: "Control de Plagas (Urgencia)",
    description: "Tratamiento de erradicación",
    status: "completed",
  },
];

const mockDocuments: Document[] = [
  {
    id: "1",
    name: "Contrato Mantenimiento 2024",
    date: "Creado • 5 Oct 2023",
    size: "1.2 MB",
  },
  {
    id: "2",
    name: "Anexo Tratamiento COVID",
    date: "Añadido • 15 Mar 2023",
    size: "856 KB",
  },
];

const getAvatarColor = (name: string): string => {
  const colors = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const getInitials = (name: string): string => {
  const words = name.split(" ");
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Clients page component
 */
export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>(mockClients);
  const [selectedClient, setSelectedClient] = useState<Client | null>(
    clients[0],
  );
  const [stateFilter, setStateFilter] = useState("Todos");
  const [provinceFilter, setProvinceFilter] = useState("Todas");
  const [sectorFilter, setSectorFilter] = useState("Hostelería");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDocTab, setActiveDocTab] = useState("contratos");
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      searchQuery === "" ||
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.nif.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesState =
      stateFilter === "Todos" || client.status === stateFilter.toLowerCase();
    return matchesSearch && matchesState;
  });

  const handleDeactivateClient = (clientId: string) => {
    setClients((prevClients) =>
      prevClients.map((client) =>
        client.id === clientId
          ? {
              ...client,
              status: client.status === "active" ? "inactive" : "active",
            }
          : client,
      ),
    );
    if (selectedClient?.id === clientId) {
      setSelectedClient((prev) =>
        prev
          ? {
              ...prev,
              status: prev.status === "active" ? "inactive" : "active",
            }
          : null,
      );
    }
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowClientModal(true);
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowClientModal(true);
  };

  const handleSaveClient = (clientData: Partial<Client>) => {
    if (editingClient) {
      // Editar cliente existente
      setClients((prevClients) =>
        prevClients.map((client) =>
          client.id === editingClient.id
            ? { ...client, ...clientData }
            : client,
        ),
      );
      if (selectedClient?.id === editingClient.id) {
        setSelectedClient((prev) => (prev ? { ...prev, ...clientData } : null));
      }
    } else {
      // Crear nuevo cliente
      const newClient: Client = {
        id: String(Date.now()),
        nif: clientData.nif || "",
        name: clientData.name || "",
        contactPerson: clientData.contactPerson || "",
        email: clientData.email || "",
        phone: clientData.phone || "",
        address: clientData.address || "",
        city: clientData.city || "",
        province: clientData.province || "",
        postalCode: clientData.postalCode || "",
        status: "active",
      };
      setClients((prevClients) => [...prevClients, newClient]);
    }
    setShowClientModal(false);
    setEditingClient(null);
  };

  return (
    <div className={styles.clientsPage}>
      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Gestión de Clientes</h2>
            <div className={styles.breadcrumb}>
              <span>Inicio</span>
              <span className={styles.breadcrumbSeparator}>›</span>
              <span className={styles.breadcrumbCurrent}>Listado</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.filterButton}>
              <Icon name="filter" size={16} />
              Limpiar filtros
            </button>
            <button
              className={styles.newClientButton}
              onClick={handleCreateClient}
            >
              <Icon name="plus" size={18} />
              Nuevo Cliente
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Estado:</label>
            <select
              className={styles.select}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option>Todos</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Provincia:</label>
            <select
              className={styles.select}
              value={provinceFilter}
              onChange={(e) => setProvinceFilter(e.target.value)}
            >
              <option>Todas</option>
              <option>Madrid</option>
              <option>Barcelona</option>
              <option>Valencia</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Sector:</label>
            <select
              className={styles.select}
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
            >
              <option>Hostelería</option>
              <option>Sanitario</option>
              <option>Alimentación</option>
              <option>Residencial</option>
            </select>
          </div>

          <div className={styles.searchBox}>
            <span className={styles.searchIcon}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Buscar cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          {filteredClients.length > 0 ? (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th>CIF / NIF</th>
                  <th>CLIENTE</th>
                  <th>CONTACTO</th>
                  <th>DIRECCIÓN</th>
                  <th>ESTADO</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {filteredClients.map((client) => (
                  <tr
                    key={client.id}
                    className={
                      selectedClient?.id === client.id ? styles.selected : ""
                    }
                    onClick={() => setSelectedClient(client)}
                  >
                    <td>
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                        {client.nif}
                      </span>
                    </td>
                    <td>
                      <div className={styles.clientInfo}>
                        <div
                          className={styles.clientAvatar}
                          style={{
                            backgroundColor: getAvatarColor(client.name),
                          }}
                        >
                          {getInitials(client.name)}
                        </div>
                        <div className={styles.clientDetails}>
                          <p className={styles.clientName}>{client.name}</p>
                          <p className={styles.clientSubtext}>
                            {client.contactPerson}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.contactInfo}>
                        <Icon
                          name="phone"
                          size={14}
                          className={styles.contactIcon}
                        />
                        {client.phone}
                      </div>
                    </td>
                    <td>
                      <div className={styles.contactInfo}>
                        <Icon
                          name="mapPin"
                          size={14}
                          className={styles.contactIcon}
                        />
                        {client.address}, {client.city}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${styles[client.status]}`}
                      >
                        <Icon
                          name={
                            client.status === "active"
                              ? "checkCircle"
                              : "xCircle"
                          }
                          size={12}
                        />
                        {client.status === "active" ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.editButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClient(client);
                          }}
                          title="Editar"
                        >
                          <Icon name="edit" size={16} />
                          Editar
                        </button>
                        <button
                          className={styles.deactivateButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeactivateClient(client.id);
                          }}
                          title={
                            client.status === "active"
                              ? "Desactivar"
                              : "Activar"
                          }
                        >
                          <Icon name="power" size={16} />
                          {client.status === "active"
                            ? "Desactivar"
                            : "Activar"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.emptyState}>
              <Icon name="users" size={48} />
              <p>No se encontraron clientes</p>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      {selectedClient && (
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarClient}>
              <div
                className={styles.sidebarAvatar}
                style={{ backgroundColor: getAvatarColor(selectedClient.name) }}
              >
                {getInitials(selectedClient.name)}
              </div>
              <div className={styles.sidebarClientInfo}>
                <h3>{selectedClient.name}</h3>
                <div className={styles.sidebarClientMeta}>
                  <span>{selectedClient.city}</span>
                  <span>•</span>
                  <span>{selectedClient.phone}</span>
                  <span>•</span>
                  <a href="#" className={styles.sidebarClientLink}>
                    Ver perfil completo
                  </a>
                  <Icon name="moreVertical" size={14} />
                </div>
              </div>
            </div>
            <button className={styles.exportButton} title="Exportar">
              <Icon name="download" size={18} />
            </button>
          </div>

          <div className={styles.sidebarContent}>
            {/* Contact Info */}
            <div className={styles.sidebarSection}>
              <h4>CONTACTO</h4>
              <div className={styles.contactList}>
                <div className={styles.contactItem}>
                  <Icon
                    name="phone"
                    size={16}
                    className={styles.contactItemIcon}
                  />
                  <span>{selectedClient.phone}</span>
                </div>
                <div className={styles.contactItem}>
                  <Icon
                    name="mail"
                    size={16}
                    className={styles.contactItemIcon}
                  />
                  <span>{selectedClient.email}</span>
                </div>
                <div className={styles.contactItem}>
                  <Icon
                    name="mapPin"
                    size={16}
                    className={styles.contactItemIcon}
                  />
                  <span>
                    {selectedClient.address}, {selectedClient.postalCode}{" "}
                    {selectedClient.city}
                  </span>
                </div>
              </div>
            </div>

            {/* Service History */}
            <div className={styles.sidebarSection}>
              <h4>HISTORIAL DE SERVICIOS</h4>
              <div className={styles.serviceHistory}>
                {mockServices.map((service, index) => (
                  <div key={service.id} className={styles.serviceItem}>
                    <div className={styles.serviceIndicator}>
                      <div
                        className={`${styles.serviceIcon} ${styles[service.status]}`}
                      />
                      {index < mockServices.length - 1 && (
                        <div className={styles.serviceLine} />
                      )}
                    </div>
                    <div className={styles.serviceContent}>
                      <p className={styles.serviceTitle}>{service.title}</p>
                      <p className={styles.serviceDescription}>
                        {service.description}
                      </p>
                      <div className={styles.serviceDate}>
                        <Icon name="calendar" size={12} />
                        {service.date} •{" "}
                        {service.status === "completed"
                          ? "Finalizado"
                          : "Programado"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents */}
            <div className={styles.documentsSection}>
              <h4>DOCUMENTACIÓN</h4>
              <div className={styles.documentTabs}>
                <button
                  className={`${styles.documentTab} ${activeDocTab === "contratos" ? styles.active : ""}`}
                  onClick={() => setActiveDocTab("contratos")}
                >
                  Contratos
                </button>
                <button
                  className={`${styles.documentTab} ${activeDocTab === "certificados" ? styles.active : ""}`}
                  onClick={() => setActiveDocTab("certificados")}
                >
                  Certificados
                </button>
                <button
                  className={`${styles.documentTab} ${activeDocTab === "inspecciones" ? styles.active : ""}`}
                  onClick={() => setActiveDocTab("inspecciones")}
                >
                  Inspecciones
                </button>
              </div>
              <div className={styles.documentList}>
                {mockDocuments.map((doc) => (
                  <div key={doc.id} className={styles.documentItem}>
                    <div className={styles.documentInfo}>
                      <Icon
                        name="document"
                        size={18}
                        className={styles.documentIcon}
                      />
                      <div className={styles.documentDetails}>
                        <p className={styles.documentName}>{doc.name}</p>
                        <p className={styles.documentMeta}>
                          {doc.date} • {doc.size}
                        </p>
                      </div>
                    </div>
                    <button className={styles.downloadButton}>
                      <Icon name="download" size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button className={styles.addDocumentButton}>
                + Subir nuevo documento
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* Client Modal */}
      <ClientModal
        isOpen={showClientModal}
        client={editingClient}
        onClose={() => {
          setShowClientModal(false);
          setEditingClient(null);
        }}
        onSave={handleSaveClient}
      />
    </div>
  );
}

export default ClientsPage;
